import {Content, ResponsiveTable, Typography, useBreakPoints} from '@hconnect/uikit'
import {PageHeader} from '@hm/fundamental'
import AddIcon from '@mui/icons-material/Add'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import ModeEditOutlineIcon from '@mui/icons-material/ModeEditOutline'
import {Alert, Box, Button, Grid, IconButton, Paper} from '@mui/material'
import {useMemo, useState} from 'react'
import {useHistory} from 'react-router-dom'

import {useTranslation} from '../../../../hooks/useTranslation'
import {useAllowedHConnectConfigCountries} from '../hooks/useAllowedHConnectConfigCountries'
import {useStyles} from '../OrderManagement/OrderChange.styles'

import {DeleteDialog} from './components/CustomerTierSettingsDeleteDialog'
import {useCustomerTierSettingsQuery} from './hooks/useCustomerTierSettingsQuery'
import {CustomerTierSettings} from './types/CustomerTierSettings'

export const CustomerTierSettingsList = () => {
  const {t} = useTranslation()
  const history = useHistory()
  const screenSizes = useBreakPoints()
  const isMobile = ['xs', 'sm'].includes(screenSizes)

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [settingsToDelete, setSettingsToDelete] = useState<string | null>(null)

  const {classes} = useStyles()

  const handleCreateCustomerTierSetting = () => {
    history.push('/configuration/hconnect/customer-tier-settings/new')
  }

  const handleOpenDeleteDialog = (tierSettingsId: string) => {
    setSettingsToDelete(tierSettingsId)
    setDeleteDialogOpen(true)
  }

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false)
    setSettingsToDelete(null)
  }

  const handleRowAction: HandleSettingsAction = (tierSettingsId, action) => {
    switch (action) {
      case 'edit':
        history.push(`/configuration/hconnect/customer-tier-settings/${tierSettingsId}/edit`)
        break
      case 'copy':
        history.push(`/configuration/hconnect/customer-tier-settings/${tierSettingsId}/copy`)
        break
      case 'delete':
        handleOpenDeleteDialog(tierSettingsId)
        break
      default:
        console.error('Unknown action:', action)
    }
  }

  const customerTierSettingsQuery = useCustomerTierSettingsQuery()
  const configurableHConnectCountries = useAllowedHConnectConfigCountries()

  const filteredAndSortedData = useMemo(() => {
    return (
      customerTierSettingsQuery.data
        ?.filter((c) => configurableHConnectCountries.includes(c.countryCode))
        .sort((a, b) => {
          return a.orgUnitId.localeCompare(b.orgUnitId)
        }) ?? []
    )
  }, [customerTierSettingsQuery.data, configurableHConnectCountries])

  return (
    <Content>
      <div className="[&_*]:!text-white">
        <PageHeader
          data-cy="page-customer-tier-settings"
          className="pl-0 pr-0 md:pl-0 md:pr-0"
          aside={
            <Button
              data-test-id="create-customerTierSetting-button"
              variant="contained"
              onClick={handleCreateCustomerTierSetting}
              className={classes.createButton}
              startIcon={<AddIcon />}
              style={{alignContent: 'center', margin: '0 auto', marginTop: isMobile ? '12px' : 0}}
            >
              {t('configurations.hconnect.customerTierSettings.createButton')}
            </Button>
          }
        >
          {t('configurations.hconnect.customerTierSettings.title')}
        </PageHeader>
      </div>

      <Grid item md={4} sm={0} xs={0}>
        <Paper elevation={4} style={{padding: isMobile ? '4px 8px' : '26px 36px'}}>
          <ResponsiveTable
            keyField="id"
            columns={CustomerTierSettingsRows(handleRowAction)}
            rows={customerTierSettingsQuery.isError ? [] : filteredAndSortedData}
            emptyMessage={
              customerTierSettingsQuery.isError ? (
                <Alert severity="error">
                  {t('configurations.hconnect.customerTierSettings.errorDataFetching')}
                </Alert>
              ) : (
                ''
              )
            }
            tableHeight="70vh"
            onSort={() => ({})}
            loading={customerTierSettingsQuery.isLoading}
            enableHorizontalScroll={true}
            stickyHeader={true}
            enablePinning={true}
            isMobile={isMobile}
            isRowSelectable={isMobile ? undefined : () => true}
          />
        </Paper>
      </Grid>
      <DeleteDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        configurationId={settingsToDelete ?? ''}
        confirmMessage={t(
          'configurations.hconnect.customerTierSettings.configurationList.deleteDialogMessage'
        )}
        dialogTitle={t(
          'configurations.hconnect.customerTierSettings.configurationList.deleteDialogTitle'
        )}
      />
    </Content>
  )
}

const CustomerTierSettingsRows = (
  handleAction: HandleSettingsAction
): {
  field: string
  headerName: string
  renderCell: (row: CustomerTierSettings) => JSX.Element
}[] => {
  const {t} = useTranslation()
  return [
    {
      field: 'action',
      headerName: t('configurations.hconnect.customerTierSettings.configurationList.action'),
      renderCell: (row: CustomerTierSettings) => (
        <Box
          style={{
            display: 'flex',
            justifyContent: 'start',
            alignItems: 'center'
          }}
        >
          <IconButton
            data-test-id={`action-edit-json-${row.id}`}
            onClick={() => handleAction(row.id, 'edit')}
            aria-label="edit"
            style={{boxShadow: 'none'}}
          >
            <ModeEditOutlineIcon style={{color: '#016AD4'}} />
          </IconButton>
          <IconButton
            data-test-id={`action-copy-json-${row.id}`}
            onClick={() => handleAction(row.id, 'copy')}
            style={{boxShadow: 'none'}}
            aria-label="copy"
          >
            <ContentCopyIcon style={{color: '#016AD4'}} />
          </IconButton>
          <IconButton
            data-test-id={`action-delete-json-${row.id}`}
            onClick={() => handleAction(row.id, 'delete')}
            style={{boxShadow: 'none'}}
            aria-label="delete"
          >
            <DeleteIcon style={{color: '#016AD4', fontSize: '24px'}} />
          </IconButton>
        </Box>
      )
    },
    {
      field: 'country',
      headerName: t('configurations.hconnect.customerTierSettings.configurationList.country'),
      renderCell: (row) => (
        <Typography data-test-id={`cell-country-${row.id}`}>{row.countryCode}</Typography>
      )
    },
    {
      field: 'orgUnitId',
      headerName: t('configurations.hconnect.customerTierSettings.configurationList.orgUnit'),
      renderCell: (row) => <Typography>{row.orgUnitId}</Typography>
    },
    {
      field: 'businessLine',
      headerName: t('configurations.hconnect.customerTierSettings.configurationList.businessLine'),
      renderCell: (row) => <Typography>{row.businessLine}</Typography>
    },
    {
      field: 'dispatchGroup',
      headerName: t('configurations.hconnect.customerTierSettings.configurationList.dispatchGroup'),
      renderCell: (row) => <Typography>{row.dispatchGroup}</Typography>
    },
    {
      field: 'tiers',
      headerName: t('configurations.hconnect.customerTierSettings.configurationList.tiers'),
      renderCell: (row) => {
        const groupedTiers: Record<
          string,
          Array<{percentage: number; leadTimeInBusinessDays: number | null | undefined}>
        > = {}

        row.tiers.forEach((tier) => {
          if (!groupedTiers[tier.name]) {
            groupedTiers[tier.name] = []
          }
          groupedTiers[tier.name].push({
            percentage: tier.percentage,
            leadTimeInBusinessDays: tier.leadTimeInBusinessDays
          })
        })

        return (
          <Box style={{whiteSpace: 'normal'}}>
            {Object.entries(groupedTiers).map(([name, entries]) => (
              <Box key={name} style={{marginBottom: '4px'}}>
                <Typography variant="body2" style={{fontWeight: 'bold'}}>
                  {name}:
                </Typography>
                {entries.map((entry, idx) => (
                  <Typography
                    key={idx}
                    variant="body2"
                    style={{
                      marginLeft: '16px',
                      color: '#016AD4'
                    }}
                  >
                    {entry.leadTimeInBusinessDays !== null &&
                    entry.leadTimeInBusinessDays !== undefined
                      ? `${entry.leadTimeInBusinessDays}d : `
                      : 'default : '}
                    {entry.percentage}%
                  </Typography>
                ))}
              </Box>
            ))}
          </Box>
        )
      }
    }
  ]
}

type HandleSettingsAction = (configurationId: string, action: 'edit' | 'copy' | 'delete') => void
